/**
 * pushClient — gestione della Push Subscription lato browser.
 *
 *  1. fetchVapidPublicKey() — recupera la VAPID key dal backend
 *  2. enablePush()          — chiede permission + crea subscription + salva su backend
 *  3. disablePush()         — rimuove subscription
 *  4. getCurrentStatus()    — info su permission + subscription attiva
 *
 *  Robusto contro vecchie subscription firmate con VAPID keys obsolete:
 *  prima di subscribe controlla che `applicationServerKey` matchi quella corrente,
 *  altrimenti rimuove la vecchia subscription e ne crea una nuova.
 */
import { api } from "@/lib/api";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const base64Std = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64Std);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function bufToB64Url(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  bytes.forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Confronta l'applicationServerKey della subscription esistente con la VAPID
 *  pubblica del backend. Ritorna true se matchano. */
function subKeyMatchesVapid(sub, vapidPublicKey) {
  try {
    const opts = sub.options;
    if (!opts || !opts.applicationServerKey) return false;
    const subKeyB64 = bufToB64Url(opts.applicationServerKey);
    // Normalizza entrambi a base64url senza padding
    const norm = (s) => s.replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_");
    return norm(subKeyB64) === norm(vapidPublicKey);
  } catch (e) {
    return false;
  }
}

export async function getCurrentStatus() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { supported: false, permission: "unsupported", subscribed: false };
  }
  const permission = Notification.permission;
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return {
    supported: true,
    permission,
    subscribed: !!sub,
  };
}

/** Wrap di una promise con timeout: rigetta dopo `ms` se non si risolve. */
function withTimeout(promise, ms, errMsg) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(errMsg || `Timeout dopo ${ms}ms`)), ms);
    promise.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}

export async function enablePush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    throw new Error("Push notifications non supportate da questo browser");
  }
  if (Notification.permission === "denied") {
    throw new Error("Le notifiche sono state bloccate. Abilita i permessi nelle impostazioni del browser.");
  }
  if (Notification.permission === "default") {
    // requestPermission con timeout: se l'utente ignora il prompt non rimaniamo bloccati
    const p = await withTimeout(
      Notification.requestPermission(),
      60_000,
      "Nessuna risposta al prompt di notifica entro 60s",
    );
    if (p !== "granted") throw new Error("Permesso notifiche negato");
  }

  // VAPID public key dal server
  const r = await withTimeout(api.get("/push/vapid-public-key"), 10_000, "Timeout caricamento VAPID");
  const publicKey = r.data?.publicKey;
  if (!publicKey) throw new Error("VAPID key non disponibile sul server");

  // Service Worker ready (timeout 10s)
  const reg = await withTimeout(
    navigator.serviceWorker.ready,
    10_000,
    "Service Worker non pronto entro 10s. Ricarica la pagina (Ctrl+Shift+R).",
  );

  // Verifica esistente subscription
  let sub = await reg.pushManager.getSubscription();

  // Se esiste ma usa una VAPID key obsoleta, la rimuovo per crearne una nuova
  // (succede dopo rotazione delle chiavi VAPID: la vecchia subscription è inutile
  // perche' il backend non riesce piu' a firmare i push per quella endpoint)
  if (sub && !subKeyMatchesVapid(sub, publicKey)) {
    try {
      // Notifica al backend che rimuove la vecchia (best effort)
      await api.post("/push/unsubscribe", {
        endpoint: sub.endpoint,
        keys: sub.toJSON().keys || { p256dh: "", auth: "" },
      }).catch(() => {});
      await sub.unsubscribe();
    } catch (e) {
      // fallback: continua, sotto si tenta resubscribe
    }
    sub = null;
  }

  if (!sub) {
    sub = await withTimeout(
      reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }),
      15_000,
      "Subscribe al servizio push non risponde. Disabilita VPN/proxy o riprova.",
    );
  }

  // Invia al backend
  const keys = sub.toJSON().keys;
  await withTimeout(
    api.post("/push/subscribe", {
      endpoint: sub.endpoint,
      keys: {
        p256dh: keys.p256dh,
        auth: keys.auth,
      },
      ua: navigator.userAgent || "",
    }),
    10_000,
    "Timeout salvataggio subscription sul server",
  );
  return true;
}

export async function disablePush() {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const keys = sub.toJSON().keys || { p256dh: "", auth: "" };
  await api.post("/push/unsubscribe", {
    endpoint: sub.endpoint,
    keys: { p256dh: keys.p256dh, auth: keys.auth },
  }).catch(() => {});
  await sub.unsubscribe();
}

export async function sendTestPush() {
  return api.post("/push/test");
}
